"use client"

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { herbicideTimingSchema } from "@/lib/validation"
import { GRASS_CHARACTERISTICS } from "@/data/grass-characteristics"
import { WEED_CHARACTERISTICS, type WeedCharacteristics } from "@/data/weed-types"
import { getRegionFromZipCode, REGION_DEFAULT_GRASS } from "@/data/regional-climate"
import { calculateHerbicideTiming, type HerbicideTimingResult } from "@/lib/herbicide-timing-calculator"
import { useSavedInputs } from "@/hooks/use-saved-inputs"
import { Checkbox } from "@/components/ui/checkbox"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { AlertCircle, Check, Lock, ChevronDown } from "lucide-react"
import { ZipCodeInput } from "@/components/ui/zip-code-input"
import { TermTooltipInline } from "@/components/tools/shared/term-tooltip"

const DIFFICULTY_LABEL: Record<"easy" | "moderate" | "difficult", string> = {
  easy: "easy to kill",
  moderate: "moderate to kill",
  difficult: "hard to kill",
}

const DIFFICULTY_SHORT_DEF = "How hard this weed is to control with herbicide."
const DIFFICULTY_LONG_DEF =
  "Easy = a single labeled application usually controls it. Moderate = needs label-rate product plus correct timing. Hard = repeat applications or a pre + post combo across the season."

type HerbicideTimingFormData = z.infer<typeof herbicideTimingSchema>
type ApplicationType = HerbicideTimingFormData["applicationType"]
type WeedTab = "common" | "easy" | "tougher"

interface HerbicideTimingFormProps {
  initialValues?: {
    zipCode?: string
    grassType?: string
    lawnSize?: number | string
    sunlight?: string
    /** Seeded from a shared/cross-tool URL (`?type=`) so a shared link
     *  reproduces the application type it was generated with. */
    applicationType?: string
    /** Seeded from a shared/cross-tool URL (`?weeds=`) so a shared link
     *  reproduces the exact weeds (and is not treated as auto-picked). */
    targetWeeds?: string[]
  }
  /** Called whenever the live calculated result changes, so the page can render
   *  the result card and downstream UI without a submit step. `weedsWereAutoPicked`
   *  is true when the user has not picked any weeds and the form auto-filled the
   *  top suggestions — lets the results UI surface a "based on X, Y, Z — change"
   *  disclosure so the result feels personalized. Result is null until the ZIP
   *  resolves to a known region (the only required input with no default).
   *  `grassTypeSource` reports whether `result.grassType` is a grass the user
   *  actually supplied (picked here, prefilled from a URL/lawn/saved input) or
   *  the region guess we substituted for the "Not Sure" default. The resolved
   *  grass on the result looks identical either way, so callers that care
   *  whether we KNOW the grass must read this. */
  onResultChange?: (
    result: HerbicideTimingResult | null,
    weedsWereAutoPicked: boolean,
    grassTypeSource: 'user' | 'default',
  ) => void
  error?: string | null
}

const COOL_SEASON_GRASSES = ["Tall Fescue", "Kentucky Bluegrass", "Perennial Ryegrass", "Fine Fescue", "Annual Ryegrass", "Mixed Cool-Season Blend"]
const WARM_SEASON_GRASSES = ["Bermudagrass", "Zoysiagrass", "St. Augustinegrass", "Centipede", "Bahiagrass", "Buffalograss", "Kikuyugrass"]

const SUGGESTED_WEED_PRIORITY: Record<ApplicationType, string[]> = {
  "pre-emergent": [
    "Crabgrass",
    "Annual Bluegrass (Poa annua)",
    "Spotted Spurge",
    "Goosegrass",
    "Foxtail (Setaria)",
    "Henbit",
  ],
  "post-emergent": [
    "Dandelions",
    "White Clover",
    "Broadleaf Plantain",
    "Creeping Charlie (Ground Ivy)",
    "Nutsedge (Yellow)",
    "Chickweed (Common)",
    "Wild Violet",
  ],
  both: ["Crabgrass", "Dandelions", "White Clover", "Spotted Spurge", "Broadleaf Plantain"],
}

function getSuggestedWeedNames(
  regionName: string,
  weeds: WeedCharacteristics[],
  applicationType: ApplicationType,
): string[] {
  const rankedNames = SUGGESTED_WEED_PRIORITY[applicationType]
  const regionalWeeds = weeds.filter((weed) =>
    regionName === "Unknown"
      ? true
      : weed.commonRegions.includes(regionName) || weed.commonRegions.includes("All regions"),
  )
  const rankedRegional = rankedNames.filter((name) =>
    regionalWeeds.some((weed) => weed.name === name),
  )
  const fallbackRegional = regionalWeeds
    .map((weed) => weed.name)
    .filter((name) => !rankedRegional.includes(name))
  return [...rankedRegional, ...fallbackRegional]
}

export function HerbicideTimingForm({ initialValues, onResultChange, error }: HerbicideTimingFormProps) {
  // Seed weeds from a shared/cross-tool URL so the result reproduces exactly and
  // is NOT treated as auto-picked. Empty -> the form auto-picks regional weeds.
  const [selectedWeeds, setSelectedWeeds] = useState<string[]>(
    initialValues?.targetWeeds ?? [],
  )
  const [activeTab, setActiveTab] = useState<WeedTab>("common")

  const seededApplicationType: ApplicationType =
    initialValues?.applicationType === "pre-emergent" ||
    initialValues?.applicationType === "post-emergent" ||
    initialValues?.applicationType === "both"
      ? initialValues.applicationType
      : "pre-emergent"

  const {
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm<HerbicideTimingFormData>({
    resolver: zodResolver(herbicideTimingSchema),
    defaultValues: {
      applicationType: seededApplicationType,
      zipCode: "",
      grassType: "Not Sure",
      targetWeeds: initialValues?.targetWeeds ?? [],
    },
  })

  const watchedZipCode = watch("zipCode")
  const watchedGrassType = watch("grassType")
  const watchedApplicationType = watch("applicationType")

  const regionName = useMemo(() => getRegionFromZipCode(watchedZipCode), [watchedZipCode])

  const isZipValid = /^\d{5}$/.test(watchedZipCode || "") && regionName !== "Unknown"

  const regionDefaultGrass = useMemo(
    () => (regionName !== "Unknown" ? REGION_DEFAULT_GRASS[regionName] || null : null),
    [regionName],
  )

  // Cross-site savedInputs prefill: only override the "Not Sure" default.
  const { savedInputs, saveInputs } = useSavedInputs()
  const seededRef = useRef(false)
  const editedRef = useRef({ zip: false, grass: false })
  useEffect(() => {
    if (initialValues?.zipCode && /^\d{5}$/.test(initialValues.zipCode) && !editedRef.current.zip) {
      setValue("zipCode", initialValues.zipCode, { shouldValidate: true })
    }
    if (initialValues?.grassType && GRASS_CHARACTERISTICS[initialValues.grassType] && !editedRef.current.grass) {
      setValue("grassType", initialValues.grassType, { shouldValidate: true })
      seededRef.current = true
    }
  }, [initialValues, setValue])
  useEffect(() => {
    if (seededRef.current) return
    const saved = savedInputs.grassType
    if (saved && GRASS_CHARACTERISTICS[saved] && (!watchedGrassType || watchedGrassType === 'Not Sure')) {
      setValue('grassType', saved)
      seededRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedInputs.grassType])
  useEffect(() => {
    if (watchedGrassType && watchedGrassType !== 'Not Sure' && GRASS_CHARACTERISTICS[watchedGrassType]) {
      saveInputs({ grassType: watchedGrassType })
    }
  }, [watchedGrassType, saveInputs])

  const handleWeedToggle = (weedName: string) => {
    const next = selectedWeeds.includes(weedName)
      ? selectedWeeds.filter((w) => w !== weedName)
      : [...selectedWeeds, weedName]
    setSelectedWeeds(next)
    setValue("targetWeeds", next, { shouldValidate: true })
  }

  const allWeeds = useMemo(() => Object.values(WEED_CHARACTERISTICS), [])
  const displayWeeds = useMemo(() => {
    if (watchedApplicationType === "pre-emergent") {
      return allWeeds.filter((w) => w.category === "pre-emergent" || w.category === "both")
    }
    if (watchedApplicationType === "post-emergent") {
      return allWeeds.filter((w) => w.category === "post-emergent" || w.category === "both")
    }
    return allWeeds
  }, [watchedApplicationType, allWeeds])

  const suggestedNames = useMemo(
    () => getSuggestedWeedNames(regionName, displayWeeds, watchedApplicationType),
    [regionName, displayWeeds, watchedApplicationType],
  )

  // ── Live calculation (re-runs on every input change) ───────────────
  // The result is meaningful only once the ZIP resolves to a known region —
  // that is the one required input with no default. Application type defaults
  // to "pre-emergent", grass defaults to the region's typical grass when left
  // on "Not Sure", and weeds auto-pick the top suggestions when none are
  // chosen. Mirrors handleFormSubmit's old resolution so the live result is
  // identical to what the submit path produced.
  const weedsWereAutoPicked = selectedWeeds.length === 0
  // Memoized so its REFERENCE is stable across re-renders. `suggestedNames.slice()`
  // would otherwise build a new array every render, churning the liveResult memo
  // below and firing onResultChange every render -> "Maximum update depth exceeded"
  // once the parent re-renders from the streamed result. selectedWeeds (useState)
  // and suggestedNames (useMemo) are both stable, so this only changes on real input.
  const resolvedWeeds = useMemo(
    () => (weedsWereAutoPicked ? suggestedNames.slice(0, 3) : selectedWeeds),
    [weedsWereAutoPicked, suggestedNames, selectedWeeds],
  )
  const resolvedGrassType = useMemo(() => {
    if (watchedGrassType && watchedGrassType !== "Not Sure") return watchedGrassType
    const region = getRegionFromZipCode(watchedZipCode)
    return REGION_DEFAULT_GRASS[region] || "Kentucky Bluegrass"
  }, [watchedGrassType, watchedZipCode])

  const liveResult = useMemo<HerbicideTimingResult | null>(() => {
    if (!isZipValid || resolvedWeeds.length === 0) return null
    try {
      return calculateHerbicideTiming(
        watchedZipCode,
        resolvedGrassType,
        resolvedWeeds,
        watchedApplicationType,
        {
          lawnSize: initialValues?.lawnSize,
          sunlight: initialValues?.sunlight ?? savedInputs.sunlight,
        },
      )
    } catch {
      return null
    }
  }, [
    isZipValid,
    watchedZipCode,
    resolvedGrassType,
    resolvedWeeds,
    watchedApplicationType,
    initialValues?.lawnSize,
    initialValues?.sunlight,
    savedInputs.sunlight,
  ])

  // Every path that fills the select (user pick, ?grass=, saved lawn, cross-tool
  // savedInputs) writes a real grass name; only the untouched default stays on
  // "Not Sure".
  const grassTypeSource: 'user' | 'default' =
    watchedGrassType && watchedGrassType !== 'Not Sure' ? 'user' : 'default'

  useEffect(() => {
    onResultChange?.(liveResult, weedsWereAutoPicked, grassTypeSource)
  }, [liveResult, weedsWereAutoPicked, grassTypeSource, onResultChange])

  const tabbedWeeds = useMemo(() => {
    const suggestedSet = new Set(suggestedNames.slice(0, 6))
    const common = displayWeeds.filter((w) => suggestedSet.has(w.name))
    const easy = displayWeeds.filter((w) => w.difficulty === "easy")
    const tougher = displayWeeds.filter((w) => w.difficulty === "difficult")
    return { common, easy, tougher }
  }, [displayWeeds, suggestedNames])

  const tabs: { k: WeedTab; label: string; shortLabel: string; count: number }[] = [
    { k: "common", label: "Most common", shortLabel: "Common", count: tabbedWeeds.common.length },
    { k: "easy", label: "Easy to kill", shortLabel: "Easy", count: tabbedWeeds.easy.length },
    { k: "tougher", label: "Tough to kill", shortLabel: "Tough", count: tabbedWeeds.tougher.length },
  ]
  const currentList = tabbedWeeds[activeTab]

  return (
    // Kept as a <form> (with no submit UI) so the postal-code input keeps its
    // accessible grouping + browser autofill context, matching the watering and
    // mowing forms. The result is live — it streams up via onResultChange.
    <form onSubmit={(e) => e.preventDefault()} className="space-y-7">
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Step 1 — ZIP */}
      <FormStep n={1} label="Where's your lawn?">
        <ZipCodeInput
          value={watchedZipCode || ""}
          onChange={(zip) => {
            editedRef.current.zip = true
            setValue("zipCode", zip, { shouldValidate: true })
          }}
          error={errors.zipCode?.message ? String(errors.zipCode.message) : undefined}
          showLabel={false}
          rememberLastZip
        />
        <Trust>We use this once to look up your region. Never stored, never sold.</Trust>
      </FormStep>

      {/* Step 2 — Intent */}
      <FormStep n={2} label="What are you trying to do?">
        <Controller
          name="applicationType"
          control={control}
          render={({ field }) => (
            <RadioGroup
              value={field.value}
              onValueChange={(value) => {
                field.onChange(value)
                setSelectedWeeds([])
                setValue("targetWeeds", [], { shouldValidate: true })
                setActiveTab("common")
              }}
              className="grid grid-cols-1 gap-2.5 sm:grid-cols-3"
            >
              <IntentChip value="pre-emergent" current={field.value} tone="prevent" label="Prevent" sub="Before they sprout" />
              <IntentChip value="post-emergent" current={field.value} tone="treat" label="Treat" sub="Weeds I can already see" />
              <IntentChip value="both" current={field.value} tone="both" label="Both" sub="Cover the whole season" />
            </RadioGroup>
          )}
        />
        {errors.applicationType && (
          <p role="alert" className="mt-2 text-sm text-red-600">
            {String(errors.applicationType.message)}
          </p>
        )}
      </FormStep>

      {/* Step 3 — Grass type */}
      <FormStep
        n={3}
        label="Your grass type"
        hint={
          regionDefaultGrass
            ? `We'll use ${regionDefaultGrass} from your ZIP if you skip this.`
            : "Skip and we'll guess from your ZIP."
        }
      >
        <div className="relative">
          <select
            value={watchedGrassType ?? ""}
            onChange={(e) => {
              editedRef.current.grass = true
              setValue("grassType", e.target.value || (undefined as unknown as string), { shouldValidate: true })
            }}
            aria-label="Grass type"
            className="w-full appearance-none rounded-lg border-[1.5px] border-gray-200 bg-white py-3.5 pl-3.5 pr-10 text-base font-medium text-gray-900 focus:border-green-600 focus:outline-none"
          >
            <option value="Not Sure">Use what's typical for my area</option>
            <optgroup label="Cool-season">
              {COOL_SEASON_GRASSES.map((g) =>
                GRASS_CHARACTERISTICS[g] ? (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ) : null,
              )}
            </optgroup>
            <optgroup label="Warm-season">
              {WARM_SEASON_GRASSES.map((g) =>
                GRASS_CHARACTERISTICS[g] ? (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ) : null,
              )}
            </optgroup>
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            aria-hidden="true"
          />
        </div>
        {errors.grassType && (
          <p role="alert" className="mt-2 text-sm text-red-600">
            {String(errors.grassType.message)}
          </p>
        )}
      </FormStep>

      {/* Step 4 — Weeds */}
      <FormStep
        n={4}
        label="Any weeds you've battled before?"
        hint="Optional. We'll start with the usual suspects for your area."
      >
        <div className="mb-3 inline-flex max-w-full rounded-full bg-gray-100 p-1">
          {tabs.map((t) => {
            const active = activeTab === t.k
            return (
              <button
                key={t.k}
                type="button"
                onClick={() => setActiveTab(t.k)}
                disabled={t.count === 0}
                className={`rounded-full px-2.5 py-2 text-xs font-semibold whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:text-gray-400 sm:px-3 ${
                  active ? "bg-white text-gray-900 shadow-xs" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <span className="sm:hidden">{t.shortLabel}</span>
                <span className="hidden sm:inline">{t.label}</span>
                <span className="ml-1 text-[10px] font-medium text-gray-400">{t.count}</span>
              </button>
            )
          })}
        </div>

        {currentList.length === 0 ? (
          <p className="rounded-lg border border-dashed border-gray-200 p-4 text-sm text-gray-500">
            Nothing in this group for the chosen intent. Try another tab.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {currentList.map((weed) => {
              const checked = selectedWeeds.includes(weed.name)
              return (
                <label
                  key={weed.name}
                  htmlFor={`weed-${weed.name}`}
                  className={`flex min-h-[60px] cursor-pointer items-start gap-3 rounded-lg border-[1.5px] p-3 transition-colors ${
                    checked
                      ? "border-green-600 bg-green-50"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <Checkbox
                    id={`weed-${weed.name}`}
                    className="mt-0.5 h-5 w-5"
                    checked={checked}
                    onCheckedChange={() => handleWeedToggle(weed.name)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-sm font-semibold text-gray-900">{weed.name}</span>
                      <TermTooltipInline
                        term="herbicide-difficulty"
                        customShortDef={DIFFICULTY_SHORT_DEF}
                        customLongDef={DIFFICULTY_LONG_DEF}
                        showLongDef
                        side="top"
                        className={`shrink-0 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-semibold border-none ${
                          weed.difficulty === "easy"
                            ? "bg-green-100 text-green-700"
                            : weed.difficulty === "moderate"
                              ? "bg-amber-100 text-amber-700"
                              : "bg-red-100 text-red-700"
                        }`}
                      >
                        {DIFFICULTY_LABEL[weed.difficulty]}
                      </TermTooltipInline>
                    </div>
                    <p className="mt-0.5 text-xs text-gray-600">{weed.notes[0]}</p>
                  </div>
                  {checked && <Check className="h-4 w-4 shrink-0 text-green-600" aria-hidden="true" />}
                </label>
              )
            })}
          </div>
        )}

        {errors.targetWeeds && (
          <p role="alert" className="mt-2 text-sm text-red-600">
            {String(errors.targetWeeds.message)}
          </p>
        )}
      </FormStep>

      {/* Live result note — the application window updates below as you go,
          no submit step. */}
      <div className="pt-2">
        <div className="flex items-center justify-center gap-1.5 text-xs text-gray-500">
          <Lock className="h-3 w-3" aria-hidden="true" />
          {!isZipValid
            ? "Enter your ZIP code to see your application window."
            : "Free, no signup. Your window updates below as you go."}
        </div>
      </div>
    </form>
  )
}

function FormStep({
  n,
  label,
  hint,
  children,
}: {
  n: number
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <div>
      <div className="mb-2.5 flex items-baseline gap-2.5">
        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-50 text-xs font-bold text-green-700">
          {n}
        </span>
        <div className="min-w-0 flex-1">
          {/* Label + hint: stacked on mobile so the label doesn't get squeezed
              into 3-5 narrow lines by the right-aligned hint. */}
          <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-3">
            <div className="text-base font-semibold text-gray-900">{label}</div>
            {hint && (
              <div className="text-xs text-gray-500 sm:ml-auto sm:text-right">{hint}</div>
            )}
          </div>
        </div>
      </div>
      <div className="pl-0 sm:pl-9">{children}</div>
    </div>
  )
}

function Trust({ children }: { children: ReactNode }) {
  return (
    <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-gray-500">
      <Lock className="h-3 w-3" aria-hidden="true" />
      {children}
    </div>
  )
}

function IntentChip({
  value,
  current,
  tone,
  label,
  sub,
}: {
  value: ApplicationType
  current: ApplicationType
  tone: "prevent" | "treat" | "both"
  label: string
  sub: string
}) {
  const active = current === value
  const accentBorder = tone === "treat" ? "border-amber-600" : "border-green-700"
  const accentBg = tone === "treat" ? "bg-amber-50" : "bg-green-50"
  const accentText = tone === "treat" ? "text-amber-700" : "text-green-700"
  return (
    <label
      htmlFor={value}
      className={`relative flex cursor-pointer flex-col gap-0.5 rounded-xl border-[1.5px] p-3.5 transition-colors ${
        active ? `${accentBorder} ${accentBg}` : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      <RadioGroupItem value={value} id={value} className="sr-only" />
      <span className={`text-sm font-bold ${active ? accentText : "text-gray-900"}`}>{label}</span>
      <span className="text-xs text-gray-500">{sub}</span>
    </label>
  )
}
